/* iter561 — DIL & NIS terminology mapping helpers.
 *
 * Single source of truth for patient-facing names of branded indices.
 * Backend always returns raw scientific values (DIL grams, NIS percent, tier).
 * This helper translates them per the active dashboard mode.
 *
 * Modes:
 *   easy     → "Инсулинов товар: 12 точки 🟢"      / "Инсулинов рейтинг: Среден 🟡"
 *   standard → "DIL: 12 единици · 27% от бюджета" / "NIS: 62% (Среден)"
 *   expert   → "DIL: 35 g · ceiling 130 g/day"    / "NIS v1.1.9: 62.4% · Medium · PIRU 28.1"
 */

export const DIL_TERM = {
  easy: {
    food: "Инсулинов товар",
    daily: "Дневен инсулинов бюджет",
    trend: "Двуседмичен инсулинов профил",
    unit: "точки",
  },
  standard: {
    food: "Инсулинов товар (DIL)",
    daily: "Дневен инсулинов бюджет",
    trend: "Двуседмичен инсулинов профил",
    unit: "единици",
  },
  expert: {
    food: "DIL (Dietary Insulin Load)",
    daily: "Personalised DIL ceiling",
    trend: "14-day DIL exposure",
    unit: "g",
  },
};

export const NIS_TERM = {
  easy: {
    label: "Инсулинов рейтинг",
    descriptor: "колко силно ястието повишава инсулина",
  },
  standard: {
    label: "Инсулинов рейтинг (NIS)",
    descriptor: "0–100% инсулиногенен потенциал",
  },
  expert: {
    label: "NIS — Nutramilo Insulin Score",
    descriptor: "exploratory methodology, not clinically validated",
  },
};

// NIS tier → Bulgarian label + color (matches design_guidelines.json urgency tokens)
export const NIS_TIER_BG = {
  Low: { label: "Нисък", color: "emerald", dot: "🟢" },
  Moderate: { label: "Умерен", color: "emerald", dot: "🟢" },
  Medium: { label: "Среден", color: "amber", dot: "🟡" },
  High: { label: "Висок", color: "orange", dot: "🟠" },
  "Very high": { label: "Много висок", color: "rose", dot: "🔴" },
};

// Other indices — patient-friendly Bulgarian display labels.
export const INDEX_LABELS_BG = {
  PRAL: "Киселинност на храненето",
  GI: "Гликемичен индекс",
  GL: "Гликемичен товар",
};

/**
 * Map a 0..100 NIS percent to a friendly tier (Bulgarian) +
 * urgency color token. Used as a single helper across Layer 1/2.
 */
export function nisTierFromPercent(percent) {
  const p = Number(percent) || 0;
  if (p < 25) return NIS_TIER_BG.Low;
  if (p < 45) return NIS_TIER_BG.Moderate;
  if (p < 65) return NIS_TIER_BG.Medium;
  if (p < 85) return NIS_TIER_BG.High;
  return NIS_TIER_BG["Very high"];
}

/**
 * Convert raw DIL grams to "points" for Layer 1 / Layer 2 displays.
 * 1:1 mapping for now — the abstraction lets us tune the perceived scale
 * later without touching every call-site.
 */
export function dilPoints(grams) {
  const g = Number(grams) || 0;
  return Math.round(g);
}

/**
 * Format a number for budget percent (used / total).
 */
export function budgetPercent(used, total) {
  const u = Number(used) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((u / t) * 100));
}

/**
 * Urgency dot from budget percent (cap → "сваляме DIL").
 *   < 60%  → green
 *   < 90%  → amber
 *   ≥ 90%  → rose
 */
export function budgetUrgency(percent) {
  const p = Number(percent) || 0;
  if (p < 60) return "emerald";
  if (p < 90) return "amber";
  return "rose";
}
